import { cva, VariantProps } from 'class-variance-authority'
import classNames from 'classnames'
import { ReactNode, useMemo, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import ReactSelect, { components, createFilter, GroupBase, Props as ReactSelectProps } from 'react-select'
import ReactSelectAsync, { AsyncProps } from 'react-select/async'
import AsyncCreatableSelect from 'react-select/async-creatable'
import ReactSelectCreatable, { CreatableProps } from 'react-select/creatable'
import { AsyncPaginate } from 'react-select-async-paginate'

const controlStyles = cva(
  // Remove hardcoded border radius from here!
  'w-full !appearance-none  font-normal  !border !border-black/30 text-[#242426] !px-2 text-sm !outline-none',
  {
    defaultVariants: {
      error: false,
      intent: 'normal',
      isDisabled: false,
      isFocused: false,
      size: 'md'
    },
    variants: {
      error: {
        true: '!border-rose-500'
      },
      intent: {
        fill: '!bg-gray-100 !border-gray-100',
        normal: ''
      },
      isDisabled: {
        true: '!bg-gray-100'
      },
      isFocused: {
        // false: '!border-[#E2E4E5]',
        true: '!border-primary'
      },
      size: {
        lg: '!min-h-16',
        md: '!min-h-11'
      }
    }
  }
)

export type SelectProps<Option, IsMulti extends boolean, Group extends GroupBase<Option>> = Omit<
  VariantProps<typeof controlStyles>,
  'error' | 'isFocused'
> &
  ReactSelectProps<Option, IsMulti, Group> &
  CreatableProps<Option, IsMulti, Group> & {
    onCreateOption?: (val?: any) => void
    isCreatable?: boolean
    /** Use with async `loadOptions` that return `{ options, hasMore, additional }` (react-select-async-paginate). */
    isPaginated?: boolean
    /** Passed through to AsyncPaginate (e.g. `{ page: 1 }`). */
    additional?: Record<string, unknown>
    debounceTimeout?: number
    label?: string
    helpText?: string
    error?: string
    isAsync?: boolean
    leftIcon?: ReactNode
    borderRadius?: string | number
    /**
     * Single-select only. On focus the current value becomes editable text: type over it to search
     * again, or delete it and leave the field to clear the value. Leaving with other text keeps the
     * previous value.
     */
    isEditable?: boolean
  }

const defaultFilter = createFilter()

/** Controlled input state that turns a single select into an editable text-like field. */
const useEditableInput = (props: any, enabled?: boolean) => {
  const [editing, setEditingState] = useState(false)
  const [inputValue, setInputValueState] = useState('')
  // react-select fires focus/blur in the same tick as a keystroke or a pick (before React re-renders),
  // so the handlers read refs rather than possibly stale state.
  const latest = useRef({ editing: false, input: '' })
  const setEditing = (value: boolean) => {
    latest.current.editing = value
    setEditingState(value)
  }
  const setInputValue = (value: string) => {
    latest.current.input = value
    setInputValueState(value)
  }
  if (!enabled) return {}

  const currentLabel: string = props.value?.label ?? ''
  const stopEditing = () => {
    setEditing(false)
    setInputValue('')
  }

  return {
    controlShouldRenderValue: !editing,
    filterOption: (option: any, input: string) =>
      input === currentLabel || (props.filterOption ?? defaultFilter)(option, input),
    inputValue,
    onBlur: (event: any) => {
      if (latest.current.editing && latest.current.input.trim() === '' && props.value) {
        props.onChange?.(null, { action: 'clear', removedValues: [props.value] })
      }
      stopEditing()
      props.onBlur?.(event)
    },
    onChange: (option: any, meta: any) => {
      stopEditing()
      props.onChange?.(option, meta)
    },
    onFocus: (event: any) => {
      // Opening the menu re-focuses the input; only the first focus should load the current label.
      if (!latest.current.editing) {
        setEditing(true)
        setInputValue(currentLabel)
      }
      props.onFocus?.(event)
    },
    onInputChange: (value: string, meta: any) => {
      if (meta.action === 'input-change') {
        if (!latest.current.editing) setEditing(true)
        setInputValue(value)
      }
      props.onInputChange?.(value, meta)
    },
    tabSelectsValue: false
  }
}

const getSelectComponent = (isAsync?: boolean, isCreatable?: boolean, isPaginated?: boolean) => {
  if (isPaginated) {
    return AsyncPaginate
  }
  if (isAsync && isCreatable) {
    return AsyncCreatableSelect
  }
  if (isAsync) {
    return ReactSelectAsync
  } else if (isCreatable) {
    return ReactSelectCreatable
  }
  return ReactSelect
}

type FormSelectProps<Option, IsMulti extends boolean, Group extends GroupBase<Option>> = {
  name: string
} & SelectProps<Option, IsMulti, Group> &
  AsyncProps<Option, IsMulti, Group>

export const Select = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  label,
  error,
  isCreatable,
  isDisabled,
  placeholder,
  helpText,
  isAsync,
  isPaginated,
  leftIcon,
  intent,
  borderRadius = 12,
  // Async selects historically preloaded on mount; pass `false` to wait until deps (e.g. another field) are set.
  defaultOptions = true,
  isEditable,
  ...props
}: SelectProps<Option, IsMulti, Group> & AsyncProps<Option, IsMulti, Group>) => {
  const SelectComponent = getSelectComponent(isAsync, isCreatable, isPaginated) as any
  const editableProps = useEditableInput(props, isEditable && !props.isMulti)
  // Stable across renders: a new component type each render would remount the input and drop focus.
  const selectComponents = useMemo(
    () => ({
      ValueContainer: ({ children, className, ...rest }: any) => (
        <components.ValueContainer
          className={classNames(className, leftIcon && '!flex items-center gap-x-1, !absolute')}
          {...rest}
        >
          {leftIcon && <span>{leftIcon}</span>}
          {children}
        </components.ValueContainer>
      )
    }),
    [leftIcon]
  )

  return (
    <div className="relative">
      {label && <p className="mb-[2px] text-sm font-medium">{label}</p>}
      <div className="">
        <SelectComponent
          {...props}
          {...editableProps}
          allowCreateWhileLoading
          classNames={{
            control: ({ isFocused, isDisabled }: { isFocused: boolean; isDisabled: boolean }) =>
              // Remove any border-radius tailwind here too
              controlStyles({ error: !!error, intent, isDisabled, isFocused }),
            indicatorSeparator: () => 'hidden',
            placeholder: () => '!text-gray-200'
          }}
          components={selectComponents}
          createOptionPosition="first"
          defaultOptions={defaultOptions}
          id={props.name}
          isDisabled={isDisabled}
          placeholder={placeholder ?? 'Select an option'}
          styles={{
            control: (base: Record<string, unknown>) => ({
              ...base,
              borderRadius: typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius,
              boxShadow: 'none',
              position: 'relative' // fallback to 12px if borderRadius is falsy
            }),
            dropdownIndicator: (base: Record<string, unknown>) => ({
              ...base,
              color: '#A79A46'
            }),
            input: (base: Record<string, unknown>) => ({
              ...base,
              'input:focus': {
                boxShadow: 'none'
              }
            }),
            menu: (base: Record<string, unknown>) => ({
              ...base,
              position: 'absolute',
              zIndex: 100000
            }),
            menuPortal: (base: Record<string, unknown>) => ({
              ...base,
              zIndex: 100000
            }),
            multiValue: (base: Record<string, unknown>) => ({
              ...base,
              backgroundColor: '#5D352B',
              borderRadius: '100px',
              color: 'white',
              marginRight: '8px !important',
              padding: '6px 8px !important'
            }),
            multiValueLabel: (base: Record<string, unknown>) => ({
              ...base,
              color: 'white',
              fontSize: '14px'
            })
          }}
        />
      </div>
      {error && (
        <p className="mt-2 text-sm text-rose-600" id={`${props.name}-error`}>
          {error}
        </p>
      )}

      {helpText && (
        <p className="mt-2 text-sm text-gray-500" id={`${props.name}-description`}>
          {helpText}
        </p>
      )}
    </div>
  )
}

export const FormSelect = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  name,
  isMulti,
  ...rest
}: FormSelectProps<Option, IsMulti, Group>) => {
  const { control } = useFormContext()
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const maybeOptions =
          (rest.options as any[]) ||
          (Array.isArray((rest as any).defaultOptions) ? ((rest as any).defaultOptions as any[]) : undefined) ||
          []

        if (isMulti) {
          const selectedValues = Array.isArray(value) ? value : []
          const selected = selectedValues.map(
            (v: any) => maybeOptions.find((o: any) => o?.value === v) || { label: String(v), value: v }
          )
          return (
            <Select<Option, IsMulti, Group>
              borderRadius={'12px'}
              error={error?.message}
              isMulti={isMulti}
              onChange={(opts: any) => onChange((opts ?? []).map((o: any) => o.value))}
              value={selected as any}
              {...rest}
            />
          )
        }

        const selected =
          value === undefined || value === null || value === ''
            ? null
            : maybeOptions.find(o => o?.value === value) || { label: String(value), value }
        return (
          <Select<Option, IsMulti, Group>
            borderRadius={'100px'}
            error={error?.message}
            isMulti={isMulti}
            onChange={(e: any) => onChange(e?.value)}
            value={selected as any}
            {...rest}
          />
        )
      }}
    />
  )
}
